import React from 'react';
import { ArrowLeft, Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';
import { getSportVocab, getSportLabel } from '../../utils/sportConfig';

export default function MeetSummary({ data, onComplete, onBack, alreadyCreated }) {
  const vocab = getSportVocab(data.sport_type);
  const sportLabel = getSportLabel(data.sport_type);
  const eventsLabel = vocab.events.charAt(0).toUpperCase() + vocab.events.slice(1);
  const calculateTotalDuration = () => {
    let totalMinutes = 0;
    data.events.forEach(event => {
      if (event.showGirls) {
        const [min, sec] = event.girlsDuration.split(':').map(Number);
        totalMinutes += (min + sec / 60) * event.girlsHeats;
      }
      if (event.showBoys) {
        const [min, sec] = event.boysDuration.split(':').map(Number);
        totalMinutes += (min + sec / 60) * event.boysHeats;
      }
      if (event.combined) {
        const [min, sec] = (event.girlsDuration || '03:00').split(':').map(Number);
        totalMinutes += (min + sec / 60) * (event.girlsHeats || 3);
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">Review Meet</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Meet Info */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-foreground mb-4">{data.name}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{data.venue}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(data.date), 'PPP')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>Starts at {data.startTime}</span>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="capitalize">
                  {sportLabel}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {data.type}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {data.level.replace('-', ' ')}
                </Badge>
                {data.sport_type === 'swim' && data.pool_lanes && (
                  <Badge variant="outline">
                    {data.pool_lanes} Lanes
                  </Badge>
                )}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total {eventsLabel}</span>
                <span className="font-semibold">{data.events.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-muted-foreground">Estimated Duration</span>
                <span className="font-semibold">{calculateTotalDuration()}</span>
              </div>
            </div>
          </Card>

          {/* Events Preview */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">{eventsLabel} Preview</h3>
            <div className="space-y-2">
              {data.events.slice(0, 10).map((event, idx) => (
                <div key={event.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <span className="text-sm font-medium text-muted-foreground w-8">{idx + 1}.</span>
                  <span className="flex-1 text-sm font-medium text-foreground">{event.name}</span>
                  <div className="flex gap-2">
                    {event.showGirls && (
                      <Badge variant="secondary" className="text-xs">Girls: {event.girlsHeats}{vocab.heat.charAt(0)}</Badge>
                    )}
                    {event.showBoys && (
                      <Badge variant="secondary" className="text-xs">Boys: {event.boysHeats}{vocab.heat.charAt(0)}</Badge>
                    )}
                  </div>
                </div>
              ))}
              {data.events.length > 10 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  And {data.events.length - 10} more {vocab.events}...
                </p>
              )}
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex">
            <Button size="lg" onClick={onComplete} className="flex-1" data-testid="meet-summary-create-btn">
              {alreadyCreated ? 'Save Changes' : 'Create Meet'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
